import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Bot, Pencil, Play, Loader2, Trash2, Link2, MessageSquare, Cpu, Settings2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { useFlaviaConfigList as useSupportIAConfigList, useSaveFlaviaConfig as useSaveSupportIAConfig, useFlaviaConfig as useSupportIAConfig, useCreateFlaviaConfig as useCreateSupportIAConfig, useDeleteFlaviaConfig, useActiveEpic, useUpdateEpic, usePerformanceTemplates, useFlowConfig, useSetFlowConfig } from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { toast } from 'sonner';

const CHAT_MODELS = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' }, // i18n-ok: nombre del modelo
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }, // i18n-ok: nombre del modelo
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }, // i18n-ok: nombre del modelo
];

const VOICE_MODELS = [
  { value: 'gpt-4o-realtime-preview', label: 'GPT-4o Realtime Preview' }, // i18n-ok: nombre del modelo
  { value: 'gpt-4o-realtime-preview-2024-10-01', label: 'GPT-4o Realtime (Oct 2024)' }, // i18n-ok: nombre del modelo
  { value: 'gpt-4o-mini-realtime-preview', label: 'GPT-4o Mini Realtime' }, // i18n-ok: nombre del modelo
];

const TRANSCRIPTION_MODELS = [
  { value: 'gpt-4o-transcribe', label: 'GPT-4o Transcribe' }, // i18n-ok: nombre del modelo
  { value: 'gpt-4o-mini-transcribe', label: 'GPT-4o Mini Transcribe' }, // i18n-ok: nombre del modelo
  { value: 'whisper-1', label: 'Whisper-1' },
];

type TFunction = ReturnType<typeof useTranslation>['t'];

// Las etiquetas llevan género/default traducibles: la const recibe la `t` del componente que la consume.
const VOICES = (t: TFunction) => [
  { value: 'Lupe', label: t('performance.adminassistants.voces.lupe') },
  { value: 'Mia', label: t('performance.adminassistants.voces.mia') },
  { value: 'Andres', label: t('performance.adminassistants.voces.andres') },
  { value: 'Lucia', label: t('performance.adminassistants.voces.lucia') },
  { value: 'Sergio', label: t('performance.adminassistants.voces.sergio') },
  { value: 'Pedro', label: t('performance.adminassistants.voces.pedro') },
];

const DEFAULT_CONFIG = {
  chatSystemPrompt: 'Sos SupportIA, asistente de evaluaciones de performance de Winclap. Tu rol es acompañar al líder en el proceso de evaluación conversacional.',
  voiceSystemPrompt: 'Sos SupportIA, asistente de evaluaciones de performance de Winclap. Respondé de forma concisa para el modo voz.',
  chatModel: 'gpt-4o',
  voiceModel: 'gpt-4o-realtime-preview',
  transcriptionModel: 'gpt-4o-transcribe',
  voice: 'Lupe',
  temperature: 0.7,
  maxContextChars: 40000,
  scoringRules: [],
  useDimensionContext: true,
  useCompetencyContext: true,
  useKnowledgeContext: false,
  dimensionContexts: { effective: '', affective: '', perspective: '' },
};

export function PerformanceAdminAssistants() {
  const { t } = useTranslation();
  const { data: assistants, isLoading } = useSupportIAConfigList();
  const deleteAssistant = useDeleteFlaviaConfig();
  const [editId, setEditId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ uuid: string; name: string } | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">{t('performance.admin.layout.assistants')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          {t('performance.admin.assistants.new_assistant')}
        </Button>
      </div>

      {/* Assistant cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(assistants ?? []).map((a) => {
          const modelLabel = CHAT_MODELS.find((m) => m.value === a.chatModel)?.label ?? a.chatModel;
          const voiceLabel = a.voice ? VOICES(t).find((v) => v.value === a.voice)?.label?.split(' — ')[0] ?? a.voice : null;
          const promptPreview = (a.chatSystemPrompt as string)?.split('\n')[0]?.slice(0, 120) ?? '';

          return (
            <Card key={a.uuid} className="group hover:shadow-xs transition-shadow">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="h-5 w-5 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm truncate">{a.name}</p>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Button variant="ghost" size="icon" aria-label={t('common.a11y.edit')} className="h-7 w-7" onClick={() => setEditId(a.uuid)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget({ uuid: a.uuid, name: a.name })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground/80 mt-0.5 line-clamp-2 leading-snug">{promptPreview}</p>

                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-500/10 text-blue-700 dark:text-blue-400 rounded-full px-2 py-0.5 font-medium">
                        {modelLabel}
                      </span>
                      {voiceLabel && (
                        <span className="inline-flex items-center gap-1 text-xs bg-violet-500/10 text-violet-700 dark:text-violet-400 rounded-full px-2 py-0.5 font-medium">
                          {voiceLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {!assistants?.length && (
          <div className="col-span-2 text-center py-12 text-muted-foreground text-sm">
            {t('performance.admin.assistants.no_assistants')}
          </div>
        )}
      </div>

      <FlowAssignmentPanel assistants={assistants ?? []} />

      {editId && <AssistantEditDialog assistantId={editId} onClose={() => setEditId(null)} />}
      {showCreate && <AssistantCreateDialog onClose={() => setShowCreate(false)} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('performance.adminassistants.eliminar_nombre', { name: deleteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('performance.adminassistants.este_asistente_se_desvinculara_de_los')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('performance.admin.assistants.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteAssistant.mutateAsync({ id: deleteTarget.uuid });
                setDeleteTarget(null);
              }}
            >
              {deleteAssistant.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FlowAssignmentPanel({ assistants }: { assistants: any[] }) {
  const { t } = useTranslation();
  const { data: activeEpic } = useActiveEpic();
  const { data: templates } = usePerformanceTemplates();
  const { data: flowConfig } = useFlowConfig();
  const updateEpic = useUpdateEpic();
  const setFlowConfig = useSetFlowConfig();

  const seasonalAssistantId = activeEpic?.assistantId ?? '';
  const lowPerfTemplate = templates?.performance;
  const lowPerfAssistantId = lowPerfTemplate?.assistantId ?? '';

  async function saveSeasonalAssistant(assistantId: string) {
    if (!activeEpic?.uuid) {
      toast.error(t('performance.adminassistants.no_hay_un_epic_activo_para'));
      return;
    }
    await updateEpic.mutateAsync({ id: activeEpic?.uuid, assistantId });
  }

  async function saveLowPerfAssistant(assistantId: string) {
    if (!lowPerfTemplate?.uuid) {
      toast.error(t('performance.adminassistants.no_hay_template_de_low_performance'));
      return;
    }
    await updateEpic.mutateAsync({ id: lowPerfTemplate.uuid, assistantId });
  }

  const isPending = updateEpic.isPending || setFlowConfig.isPending;

  const flows = [
    {
      key: 'seasonal',
      name: 'EPIC',
      description: t('performance.adminassistants.evaluacion_de_competencias_con_puntaje_numerico'),
      currentId: seasonalAssistantId,
      onSave: saveSeasonalAssistant,
      disabled: !activeEpic?.uuid,
      disabledNote: 'Sin EPIC activo',
    },
    {
      key: 'low_perf',
      name: 'Low Performance',
      description: t('performance.adminassistants.seguimiento_de_bajo_desempeno_con_plan'),
      currentId: lowPerfAssistantId,
      onSave: saveLowPerfAssistant,
      disabled: !lowPerfTemplate?.uuid,
      disabledNote: 'Template no encontrado',
    },
    {
      key: 'dtalk',
      name: 'D-Talk',
      description: t('performance.adminassistants.conversacion_de_desarrollo_sin_puntaje_numerico'),
      currentId: flowConfig?.dtalkAssistantId ?? '',
      onSave: (assistantId: string) => setFlowConfig.mutateAsync({ flow: 'dtalk', assistantId }),
      disabled: false,
      disabledNote: '',
    },
    {
      key: 'talentMap',
      name: 'Mapa de Talento',
      description: t('performance.adminassistants.ubicacion_en_matriz_de_potencial_desempeno'),
      currentId: flowConfig?.talentMapAssistantId ?? '',
      onSave: (assistantId: string) => setFlowConfig.mutateAsync({ flow: 'talentMap', assistantId }),
      disabled: false,
      disabledNote: '',
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">{t('performance.adminassistants.asignacion_de_flujos')}</h2>
      </div>

      <Card>
        <CardContent className="pt-0 pb-0 divide-y divide-border">
          {flows.map((flow) => (
            <FlowRow key={flow.key} flow={flow} assistants={assistants} isPending={isPending} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function FlowRow({ flow, assistants, isPending }: { flow: any; assistants: any[]; isPending: boolean }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(flow.currentId || '');

  useEffect(() => {
    if (flow.currentId) setSelected(flow.currentId);
  }, [flow.currentId]);

  const currentAssistant = assistants.find((a) => a.uuid === flow.currentId);

  return (
    <div className={`flex items-center gap-3 py-3 px-1 ${flow.disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{flow.name}</p>
        <p className="text-xs text-muted-foreground">
          {flow.disabled ? flow.disabledNote : (currentAssistant ? currentAssistant.name : t('performance.adminassistants.sin_asistente'))}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Select value={selected} onValueChange={setSelected} disabled={flow.disabled}>
          <SelectTrigger className="h-8 text-xs w-44">
            <SelectValue placeholder={t('performance.adminassistants.selecciona')} />
          </SelectTrigger>
          <SelectContent>
            {assistants.map((a) => (
              <SelectItem key={a.uuid} value={a.uuid}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs px-3 shrink-0"
          disabled={flow.disabled || !selected || selected === flow.currentId || isPending}
          onClick={() => flow.onSave(selected)}
        >
          {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}

function VoicePreviewButton({ voice }: { voice: string }) {
  const { t } = useTranslation();
  const previewMutation = trpc.performance.flaviaConfig.previewVoice.useMutation();

  async function handlePlay() {
    const result = await previewMutation.mutateAsync({ voice });
    const bytes = Uint8Array.from(atob(result.audio), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: result.mimeType });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.play();
    audio.onended = () => URL.revokeObjectURL(url);
  }

  return (
    <button
      type="button"
      onClick={handlePlay}
      disabled={previewMutation.isPending}
      className="flex items-center gap-1 text-xs text-brand-green hover:text-[#16B0A2] disabled:opacity-50 transition-colors"
      title={t('performance.adminassistants.escuchar_voz', { voice })}
    >
      {previewMutation.isPending
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <Play className="h-3 w-3" />
      }
      Escuchar
    </button>
  );
}

function ConfigFields({ form, setForm }: { form: any; setForm: (fn: (p: any) => any) => void }) {
  const { t } = useTranslation();

  return (
    <Tabs defaultValue="prompts" className="w-full">
      <TabsList className="w-full grid grid-cols-3 mb-4">
        <TabsTrigger value="prompts" className="gap-1.5 text-xs">
          <MessageSquare className="h-3.5 w-3.5" />
          Prompts
        </TabsTrigger>
        <TabsTrigger value="models" className="gap-1.5 text-xs">
          <Cpu className="h-3.5 w-3.5" />
          Modelos
        </TabsTrigger>
        <TabsTrigger value="context" className="gap-1.5 text-xs">
          <Settings2 className="h-3.5 w-3.5" />
          Contexto
        </TabsTrigger>
      </TabsList>

      {/* ── Prompts ── */}
      <TabsContent value="prompts" className="space-y-4 mt-0">
        <div>
          <Label className="text-sm font-medium">{t('performance.admin.assistants.chat_prompt_label')}</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{t('performance.adminassistants.se_inyecta_en_todas_las_conversaciones')}</p>
          <Textarea
            value={form.chatSystemPrompt ?? ''}
            onChange={(e) => setForm((p: any) => ({ ...p, chatSystemPrompt: e.target.value }))}
            rows={8}
            className="font-mono text-xs resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">{(form.chatSystemPrompt ?? '').length} caracteres</p>
        </div>
        <div>
          <Label className="text-sm font-medium">{t('performance.admin.assistants.voice_prompt_label')}</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{t('performance.adminassistants.prompt_corto_para_el_modo_voz')}</p>
          <Textarea
            value={form.voiceSystemPrompt ?? ''}
            onChange={(e) => setForm((p: any) => ({ ...p, voiceSystemPrompt: e.target.value }))}
            rows={4}
            className="font-mono text-xs resize-none"
          />
        </div>
      </TabsContent>

      {/* ── Modelos ── */}
      <TabsContent value="models" className="space-y-5 mt-0">
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('performance.adminassistants.chat_transcripcion')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('performance.admin.assistants.chat_model_label')}</Label>
              <Select value={form.chatModel ?? 'gpt-4o'} onValueChange={(v) => setForm((p: any) => ({ ...p, chatModel: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHAT_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('performance.adminassistants.transcripcion')}</Label>
              <Select value={form.transcriptionModel ?? 'gpt-4o-transcribe'} onValueChange={(v) => setForm((p: any) => ({ ...p, transcriptionModel: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSCRIPTION_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('performance.adminassistants.voz_realtime')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('performance.adminassistants.modelo_realtime')}</Label>
              <Select value={form.voiceModel ?? 'gpt-4o-realtime-preview'} onValueChange={(v) => setForm((p: any) => ({ ...p, voiceModel: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOICE_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">{t('performance.admin.assistants.voice_label_openai')}</Label>
                <VoicePreviewButton voice={form.voice ?? 'Lupe'} />
              </div>
              <Select value={form.voice ?? 'Lupe'} onValueChange={(v) => setForm((p: any) => ({ ...p, voice: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOICES(t).map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('performance.adminassistants.parametros')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('performance.admin.assistants.temperature_label')}</Label>
              <p className="text-xs text-muted-foreground mb-1">{t('performance.adminassistants.0_deterministico_2_creativo')}</p>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={form.temperature ?? 0.7}
                onChange={(e) => setForm((p: any) => ({ ...p, temperature: parseFloat(e.target.value) }))}
              />
            </div>
            <div>
              <Label className="text-xs">{t('performance.admin.assistants.max_context_label')}</Label>
              <p className="text-xs text-muted-foreground mb-1">{t('performance.adminassistants.caracteres_maximos_de_historial')}</p>
              <Input
                className="h-8 text-xs"
                type="number"
                value={form.maxContextChars ?? 40000}
                onChange={(e) => setForm((p: any) => ({ ...p, maxContextChars: parseInt(e.target.value) }))}
              />
            </div>
          </div>
        </div>
      </TabsContent>

      {/* ── Contexto ── */}
      <TabsContent value="context" className="space-y-4 mt-0">
        <p className="text-xs text-muted-foreground">{t('performance.adminassistants.controla_que_informacion_adicional_se_inyecta')}</p>
        <div className="rounded-lg border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-sm font-medium">{t('performance.admin.assistants.use_dimension_context')}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t('performance.adminassistants.inyecta_las_dimensiones_y_criterios_del')}</p>
            </div>
            <Switch
              checked={form.useDimensionContext ?? true}
              onCheckedChange={(v) => setForm((p: any) => ({ ...p, useDimensionContext: v }))}
            />
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function AssistantEditDialog({ assistantId, onClose }: { assistantId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: config, isLoading } = useSupportIAConfig(assistantId);
  const saveConfig = useSaveSupportIAConfig();
  const [form, setForm] = useState<any>(null);

  useEffect(() => {
    if (config && !form) setForm({ ...DEFAULT_CONFIG, ...config });
  }, [config]);

  if (isLoading || !form) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent><div className="flex items-center justify-center py-8"><WinclapLoader size={32} className="text-foreground/25" /></div></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            {t('performance.admin.assistants.edit_title_prefix')} {form.name}
          </DialogTitle>
        </DialogHeader>
        <div className="py-1">
          <div className="mb-4">
            <Label className="text-xs">{t('performance.adminassistants.nombre_del_asistente')}</Label>
            <Input className="mt-1 h-8 text-sm" value={form.name ?? ''} onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))} />
          </div>
          <ConfigFields form={form} setForm={setForm} />
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            size="sm"
            onClick={async () => {
              await saveConfig.mutateAsync({ ...form, id: assistantId });
              onClose();
            }}
            disabled={saveConfig.isPending}
          >
            {saveConfig.isPending ? <WinclapLoader size={14} /> : t('performance.adminassistants.guardar_cambios')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssistantCreateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const createConfig = useCreateSupportIAConfig();
  const [name, setName] = useState('');
  const [form, setForm] = useState<any>({ ...DEFAULT_CONFIG });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            {t('performance.admin.assistants.new_assistant')}
          </DialogTitle>
        </DialogHeader>
        <div className="py-1">
          <div className="mb-4">
            <Label className="text-xs">{t('performance.adminassistants.nombre_del_asistente')}</Label>
            <Input className="mt-1 h-8 text-sm" placeholder={t('performance.adminassistants.ej_flavia_evaluacion_q3')} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <ConfigFields form={form} setForm={setForm} />
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            size="sm"
            onClick={async () => {
              await createConfig.mutateAsync({ ...form, name });
              onClose();
            }}
            disabled={createConfig.isPending || !name.trim()}
          >
            {createConfig.isPending ? <WinclapLoader size={14} /> : t('performance.adminassistants.crear_asistente')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
